import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { acknowledgeNoteAction, executeReviewedNoteAction, invokeNoteAction, newNoteOperationId, noteOperationKey, noteOwnerSession, parseNoteRecord, readPendingNoteAction, NoteExecutorError,
  type NoteRequest, type NoteRecord, type NoteSnapshot } from "@/lib/noteExecutor";
import { canonicalNoteRecord, canonicalNoteSnapshot, NOTE_SELECT_FIELDS, noteId, parseNoteActionRequest } from "../../supabase/functions/_shared/noteActionDomain";
import { subscribeNoteChanges } from "@/lib/noteChanges";

export type NoteType = "idea" | "insight";
export type Note = NoteRecord;
export type { NoteSnapshot };
export const NOTE_PAGE_SIZE = 100;
export interface CreateNoteOptions { id?: string; source?: "manual" | "voice"; operation_id?: string }
interface Cursor { id: string; created_at: string }
function intent(request: NoteRequest): string {
  const generatedKey = request.action === "create" && request.client_action_id === noteOperationKey(request.operation_id!, "create");
  return JSON.stringify(Object.fromEntries(Object.entries(request).filter(([key]) => !["operation_id", "idempotency_key"].includes(key) && !(generatedKey && key === "client_action_id")).sort(([a], [b]) => a.localeCompare(b))));
}
interface UseNotesReturn {
  notes: Note[]; userId: string | null; loading: boolean; loadError: boolean; saving: boolean; hasMore: boolean;
  addNote(content: string, type: NoteType, options?: CreateNoteOptions): Promise<Note | null>;
  updateNote(id: string, content: string, snapshot: NoteSnapshot): Promise<Note>;
  deleteNote(id: string, snapshot: NoteSnapshot): Promise<void>;
  markSentToMirror(id: string, snapshot: NoteSnapshot): Promise<Note>;
  refresh(): Promise<void>; loadMore(): Promise<void>;
}
export function snapshotForNote(note: Note): NoteSnapshot | null {
  try {
    const record = canonicalNoteRecord({ note: Object.fromEntries(NOTE_SELECT_FIELDS.split(",").map(key => [key, (note as unknown as Record<string, unknown>)[key]])) }, note.user_id);
    return record.writable && !record.read_only ? record.snapshot : null;
  } catch { return null; }
}

export const useNotes = (): UseNotesReturn => {
  const [notes, setNotes] = useState<Note[]>([]), [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true), [loadError, setLoadError] = useState(false), [saving, setSaving] = useState(false), [hasMore, setHasMore] = useState(false);
  const owner = useRef<string | null | undefined>(undefined), generation = useRef(0), mounted = useRef(false), readVersion = useRef(0), cursor = useRef<Cursor | null>(null), writing = useRef(false);
  const capture = useCallback(() => {
    const uid = owner.current, epoch = generation.current;
    const assert = () => { if (!mounted.current || !uid || owner.current !== uid || generation.current !== epoch) throw new NoteExecutorError("invalid_session"); };
    assert(); return { uid: uid!, assert };
  }, []);
  const fetchOwned = useCallback(async (append: boolean) => {
    const { uid, assert } = capture(), requestVersion = ++readVersion.current, after = append ? cursor.current : null;
    if (append && !after) return;
    setLoading(true); setLoadError(false);
    try {
      await noteOwnerSession(uid, assert);
      let query = supabase.from("notes").select(NOTE_SELECT_FIELDS).eq("user_id", uid);
      if (after) {
        // .or() uses raw PostgREST syntax: these values come only from a validated
        // owned row and a strict scalar allowlist, never user/model input.
        if (!/^[0-9TtZz:.+ -]{1,100}$/.test(after.created_at)) throw new NoteExecutorError("note_read_unconfirmed");
        query = query.or("created_at.lt." + after.created_at + ",and(created_at.eq." + after.created_at + ",id.lt." + noteId(after.id) + ")");
      }
      assert();
      const response = await query.order("created_at", { ascending: false }).order("id", { ascending: false }).limit(NOTE_PAGE_SIZE + 1);
      assert(); if (requestVersion !== readVersion.current) return;
      if (response.error || !Array.isArray(response.data) || response.data.length > NOTE_PAGE_SIZE + 1) throw new NoteExecutorError("note_read_unconfirmed");
      const records = response.data.map(note => canonicalNoteRecord({ note }, uid));
      if (new Set(records.map(note => note.id)).size !== records.length) throw new NoteExecutorError("note_read_unconfirmed");
      const page = records.slice(0, NOTE_PAGE_SIZE), more = records.length > NOTE_PAGE_SIZE, last = page.at(-1);
      cursor.current = more && last ? { id: last.id, created_at: last.created_at } : null;
      setHasMore(more);
      if (append) setNotes(previous => { const merged = new Map(previous.map(note => [note.id, note])); for (const note of page) merged.set(note.id, note); return [...merged.values()]; });
      else setNotes(page);
    } catch (cause) {
      try { assert(); } catch { return; }
      if (requestVersion !== readVersion.current) return;
      console.error("[useNotes] owned read unavailable"); setLoadError(true);
      throw cause;
    } finally { try { assert(); if (requestVersion === readVersion.current) setLoading(false); } catch { /* A different account owns the active state. */ } }
  }, [capture]);
  const refresh = useCallback(async () => { try { await fetchOwned(false); } catch { /* The visible loadError offers retry; previous owned rows are preserved. */ } }, [fetchOwned]);
  const loadMore = useCallback(async () => { if (loading || !hasMore) return; try { await fetchOwned(true); } catch { /* The same owned retry state covers continuation failure. */ } }, [fetchOwned, loading, hasMore]);
  useEffect(() => {
    mounted.current = true; let changed = false;
    const apply = (uid: string | null) => {
      if (!mounted.current) return;
      if (uid !== owner.current) {
        generation.current++; readVersion.current++; owner.current = uid; cursor.current = null; writing.current = false;
        setNotes([]); setHasMore(false); setSaving(false); setUserId(uid);
      }
      if (!uid) { setLoading(false); setLoadError(true); return; }
      void refresh();
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      changed = true; const uid = session?.user.id ?? null;
      if (uid === owner.current) return; apply(uid);
    });
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted.current || changed) return;
      if (error) { setLoading(false); setLoadError(true); return; }
      apply(data.session?.user.id ?? null);
    }).catch(() => { if (mounted.current && !changed) { console.error("[useNotes] session unavailable"); setLoading(false); setLoadError(true); } });
    const unsubscribe = subscribeNoteChanges(uid => { if (owner.current === uid && mounted.current) void refresh(); });
    const identityLifetime = generation, readLifetime = readVersion;
    return () => { mounted.current = false; identityLifetime.current++; readLifetime.current++; subscription.unsubscribe(); unsubscribe(); };
  }, [refresh]);

  const mutate = useCallback(async (request: NoteRequest, uid: string, assert: () => void): Promise<Note | null> => {
    assert(); if (writing.current) throw new NoteExecutorError("note_action_busy");
    writing.current = true; setSaving(true);
    try {
      const pending = await readPendingNoteAction(uid); assert();
      if (pending) {
        const frozen = parseNoteActionRequest(pending.body);
        if (frozen.mirror_context || intent(frozen) !== intent(request)) throw new NoteExecutorError("note_pending_action");
        // Keep the literal journal body, including property order; canonical
        // intent comparison is only a guard and cannot rebuild the operation.
        request = structuredClone(pending.body) as unknown as NoteRequest;
      }
      const { entry } = await executeReviewedNoteAction(uid, request, assert); assert();
      let current: Note | null = null;
      if (request.action === "delete") {
        const absence = await supabase.from("notes").select("id").eq("user_id", uid).eq("id", request.note_id!).maybeSingle(); assert();
        if (absence.error || absence.data) throw new NoteExecutorError("note_result_unconfirmed");
      } else {
        const response = await invokeNoteAction({ action: "get", note_id: request.note_id }, uid, assert); assert();
        current = parseNoteRecord(response.note);
        if (!current || current.id !== request.note_id || current.user_id !== uid) throw new NoteExecutorError("note_result_unconfirmed");
      }
      // A receipt may be historical. Load current owned rows rather than applying
      // its old snapshot to the module or marking a flag optimistically.
      await fetchOwned(false); assert();
      await acknowledgeNoteAction(entry, assert); assert();
      return current;
    } finally { try { assert(); writing.current = false; setSaving(false); } catch { /* Keep a completed old-owner journal recoverable. */ } }
  }, [fetchOwned]);
  const selectedRequest = useCallback((action: "update" | "delete" | "send_to_mirror", id: string, snapshot: NoteSnapshot, content?: string) => {
    const { uid, assert } = capture();
    if (!snapshot) throw new NoteExecutorError("incomplete_note_snapshot");
    const reviewed = canonicalNoteSnapshot(snapshot, uid);
    if (reviewed.note.id !== noteId(id) || !canonicalNoteRecord(reviewed, uid).writable) throw new NoteExecutorError("note_read_only");
    const request = parseNoteActionRequest({ action, note_id: id, operation_id: newNoteOperationId(), confirmed: true, expected_snapshot: reviewed, ...(content !== undefined ? { content } : {}) });
    return { uid, assert, request };
  }, [capture]);
  const addNote = useCallback(async (content: string, type: NoteType, options: CreateNoteOptions = {}): Promise<Note | null> => {
    const { uid, assert } = capture(), operation = options.operation_id ?? newNoteOperationId();
    const request = parseNoteActionRequest({ action: "create", note_id: options.id ?? newNoteOperationId(), operation_id: operation, confirmed: true,
      content, type, source: options.source ?? "manual", client_action_id: noteOperationKey(operation, "create") });
    return mutate(request, uid, assert);
  }, [capture, mutate]);
  const updateNote = useCallback(async (id: string, content: string, snapshot: NoteSnapshot): Promise<Note> => {
    const { uid, assert, request } = selectedRequest("update", id, snapshot, content); return (await mutate(request, uid, assert))!;
  }, [selectedRequest, mutate]);
  const deleteNote = useCallback(async (id: string, snapshot: NoteSnapshot): Promise<void> => {
    const { uid, assert, request } = selectedRequest("delete", id, snapshot); await mutate(request, uid, assert);
  }, [selectedRequest, mutate]);
  const markSentToMirror = useCallback(async (id: string, snapshot: NoteSnapshot): Promise<Note> => {
    const { uid, assert, request } = selectedRequest("send_to_mirror", id, snapshot); return (await mutate(request, uid, assert))!;
  }, [selectedRequest, mutate]);
  return { notes, userId, loading, loadError, saving, hasMore, addNote, updateNote, deleteNote, markSentToMirror, refresh, loadMore };
};
